<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { format } from 'date-fns';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  from: {
    type: Number,
    default: 0,
  },
  to: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'confirm']);

const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');
const agents = useMapGetter('agents/getAgents');

const dialogRef = ref(null);
const selectedInbox = ref('');
const selectedTeam = ref('');
const selectedAgent = ref('');
const selectedStatus = ref('');

const STATUS_OPTIONS = [
  { value: 'open', labelKey: 'REPORT.DETAILED_EXPORT.STATUS_OPTIONS.OPEN' },
  {
    value: 'resolved',
    labelKey: 'REPORT.DETAILED_EXPORT.STATUS_OPTIONS.RESOLVED',
  },
  {
    value: 'pending',
    labelKey: 'REPORT.DETAILED_EXPORT.STATUS_OPTIONS.PENDING',
  },
  {
    value: 'snoozed',
    labelKey: 'REPORT.DETAILED_EXPORT.STATUS_OPTIONS.SNOOZED',
  },
];

const formattedPeriod = computed(() => {
  if (!props.from || !props.to) return '';
  const fromDate = format(new Date(props.from * 1000), 'yyyy-MM-dd');
  const toDate = format(new Date(props.to * 1000), 'yyyy-MM-dd');
  return `${fromDate} – ${toDate}`;
});

const canExport = computed(() => Boolean(props.from && props.to));

const handleConfirm = () => {
  if (!canExport.value) return;
  emit('confirm', {
    inboxId: selectedInbox.value || null,
    teamId: selectedTeam.value || null,
    assigneeId: selectedAgent.value || null,
    status: selectedStatus.value || null,
  });
};

const resetSelections = () => {
  selectedInbox.value = '';
  selectedTeam.value = '';
  selectedAgent.value = '';
  selectedStatus.value = '';
};

const handleClose = () => {
  resetSelections();
  emit('close');
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
  store.dispatch('agents/get');
});

watch(
  () => props.show,
  newValue => {
    if (newValue) {
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('REPORT.DETAILED_EXPORT.TITLE')"
    :description="$t('REPORT.DETAILED_EXPORT.DESCRIPTION')"
    :cancel-button-label="$t('REPORT.DETAILED_EXPORT.CANCEL')"
    :confirm-button-label="$t('REPORT.DETAILED_EXPORT.DOWNLOAD')"
    :disable-confirm-button="!canExport"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('REPORT.DETAILED_EXPORT.PERIOD_LABEL') }}
        </label>
        <div
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-alpha-black1 text-n-slate-11"
        >
          {{ formattedPeriod }}
        </div>
      </div>

      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('REPORT.DETAILED_EXPORT.INBOX') }}
        </label>
        <select
          v-model="selectedInbox"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
        >
          <option value="">
            {{ $t('REPORT.DETAILED_EXPORT.ALL') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('REPORT.DETAILED_EXPORT.TEAM') }}
        </label>
        <select
          v-model="selectedTeam"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
        >
          <option value="">
            {{ $t('REPORT.DETAILED_EXPORT.ALL') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
      </div>

      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('REPORT.DETAILED_EXPORT.AGENT') }}
        </label>
        <select
          v-model="selectedAgent"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
        >
          <option value="">
            {{ $t('REPORT.DETAILED_EXPORT.ALL') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </div>

      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('REPORT.DETAILED_EXPORT.STATUS') }}
        </label>
        <select
          v-model="selectedStatus"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
        >
          <option value="">
            {{ $t('REPORT.DETAILED_EXPORT.ALL') }}
          </option>
          <option
            v-for="option in STATUS_OPTIONS"
            :key="option.value"
            :value="option.value"
          >
            {{ $t(option.labelKey) }}
          </option>
        </select>
      </div>
    </div>
  </Dialog>
</template>
